import { FiCalendar, FiCheckCircle, FiClock, FiPlay, FiSkipForward } from "react-icons/fi";
import type { Meeting } from "../../types/meeting.type";

interface MeetingStatusBannerProps {
  meeting: Meeting;
}

interface StatusConfig {
  bgClass: string;
  borderClass: string;
  icon: React.ReactNode;
  label: string;
  textClass: string;
}

const STATUS_CONFIG: Record<Meeting["status"], StatusConfig> = {
  draft: {
    label: "Setting Up Meeting",
    icon: <FiCalendar className="h-4 w-4" />,
    bgClass: "bg-gray-50",
    borderClass: "border-gray-200",
    textClass: "text-gray-700",
  },
  invited: {
    label: "Invitations Sent",
    icon: <FiClock className="h-4 w-4" />,
    bgClass: "bg-yellow-50",
    borderClass: "border-yellow-200",
    textClass: "text-yellow-700",
  },
  in_progress: {
    label: "Meeting In Progress",
    icon: <FiPlay className="h-4 w-4" />,
    bgClass: "bg-green-50",
    borderClass: "border-green-200",
    textClass: "text-green-700",
  },
  completed: {
    label: "Meeting Completed",
    icon: <FiCheckCircle className="h-4 w-4" />,
    bgClass: "bg-green-50",
    borderClass: "border-green-200",
    textClass: "text-green-700",
  },
  overridden: {
    label: "Meeting Skipped",
    icon: <FiSkipForward className="h-4 w-4" />,
    bgClass: "bg-gray-50",
    borderClass: "border-gray-200",
    textClass: "text-gray-700",
  },
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) {
    return "";
  }
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const MeetingStatusBanner: React.FC<MeetingStatusBannerProps> = ({ meeting }) => {
  const config = STATUS_CONFIG[meeting.status];

  return (
    <div
      data-testid="meeting-status-banner"
      className={`flex items-start gap-3 rounded-lg border p-4 ${config.bgClass} ${config.borderClass}`}
    >
      <span className={config.textClass}>{config.icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${config.textClass}`}>{config.label}</p>
        {meeting.scheduled_at && (
          <p className="text-xs text-gray-500 mt-0.5">
            Scheduled: {formatDateTime(meeting.scheduled_at)}
            {meeting.timezone ? ` (${meeting.timezone})` : ""}
          </p>
        )}
        {meeting.location && (
          <p className="text-xs text-gray-500 mt-0.5">Location: {meeting.location}</p>
        )}
        {meeting.status === "overridden" && meeting.override_reason && (
          <p className="text-xs text-gray-500 mt-0.5">Reason: {meeting.override_reason}</p>
        )}
        {meeting.status === "completed" && meeting.completed_at && (
          <p className="text-xs text-gray-500 mt-0.5">
            Completed: {formatDateTime(meeting.completed_at)}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-0.5">
          {meeting.attendee_count} attendee{meeting.attendee_count !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
};
